/**
 * LockManager - Client-side lock management for Phase 4 Collaboration
 *
 * Manages block locks via PageLocksChannel WebSocket connection.
 * Uses block-level lifecycle (not field-level) per commentary guidance.
 *
 * Key features:
 * - Acquire/release locks on blocks
 * - Auto-extend locks every 20s while holding
 * - Track locks held by other admins
 * - Integrate with SaveQueue before releasing
 */

// Lock extend interval (20s, lock expires in 30s)
const LOCK_EXTEND_INTERVAL = 20000

// Debug logging helper - only log in debug mode or localhost
const DEBUG = typeof window !== 'undefined' && (window.PAGE_EDITOR_DEBUG || window.location?.hostname === 'localhost')
const debugLog = (...args) => DEBUG && console.log(...args)

/**
 * @typedef {Object} LockInfo
 * @property {string} block_id - Block UUID
 * @property {string} admin_id - Admin UUID holding lock
 * @property {string} admin_name - Admin full name
 * @property {string} expires_at - ISO8601 expiration timestamp
 * @property {boolean} owned - Whether current admin owns this lock
 */

export class LockManager {
  /**
   * @param {Object} options
   * @param {string} options.pageId - Current page UUID
   * @param {string} options.currentAdminId - Current admin UUID
   * @param {Object} options.consumer - ActionCable consumer instance
   * @param {Object} [options.saveQueue] - Optional SaveQueue instance for release checks
   */
  constructor(options = {}) {
    this.pageId = options.pageId
    this.currentAdminId = options.currentAdminId
    this.consumer = options.consumer
    this.saveQueue = options.saveQueue

    // Lock state
    /** @type {Map<string, LockInfo>} */
    this.locks = new Map()
    /** @type {Set<string>} Block IDs we hold locks on */
    this.myLocks = new Set()
    /** @type {Map<string, number>} Block ID -> extend interval timer ID */
    this.extendIntervals = new Map()
    /** @type {Map<string, number>} P0-4 FIX: Block ID -> expiration timeout ID for client-side expiry */
    this.expirationTimeouts = new Map()

    // Callbacks
    /** @type {Set<Function>} Lock change listeners */
    this.lockChangeListeners = new Set()

    // Channel subscription
    this.subscription = null

    // Pending lock requests (Promise resolvers)
    /** @type {Map<string, {resolve: Function, reject: Function}>} */
    this.pendingLockRequests = new Map()

    debugLog('[LockManager] Initialized for page:', this.pageId)
  }

  /**
   * Connect to PageLocksChannel
   * @returns {Promise<void>}
   */
  connect() {
    return new Promise((resolve, reject) => {
      if (!this.consumer) {
        reject(new Error('ActionCable consumer required'))
        return
      }

      this.subscription = this.consumer.subscriptions.create(
        { channel: 'PageLocksChannel', page_id: this.pageId },
        {
          connected: () => {
            debugLog('[LockManager] Connected to PageLocksChannel')
            resolve()
          },
          disconnected: () => {
            debugLog('[LockManager] Disconnected from PageLocksChannel')
            // Clear locks on disconnect - they'll be refreshed on reconnect
            this.locks.clear()
            this.myLocks.clear()
            this.notifyLockChange()
          },
          rejected: () => {
            console.error('[LockManager] PageLocksChannel subscription rejected')
            reject(new Error('Channel subscription rejected'))
          },
          received: (data) => {
            this.handleMessage(data)
          }
        }
      )
    })
  }

  /**
   * Disconnect from channel
   * P1-8 FIX: Force-releases all locks on disconnect (ignores pending saves)
   */
  disconnect() {
    // Release all our locks before disconnecting (force release ignores pending saves)
    for (const blockId of this.myLocks) {
      this.releaseLock(blockId, true) // P1-8 FIX: force release on disconnect
    }

    // Clear extend intervals
    for (const [, timerId] of this.extendIntervals) {
      clearInterval(timerId)
    }
    this.extendIntervals.clear()

    // P0-4 FIX: Clear expiration timeouts
    for (const [, timerId] of this.expirationTimeouts) {
      clearTimeout(timerId)
    }
    this.expirationTimeouts.clear()

    // Unsubscribe
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    debugLog('[LockManager] Disconnected')
  }

  /**
   * Acquire a lock on a block
   * @param {string} blockId - Block UUID
   * @returns {Promise<boolean>} True if lock acquired
   */
  acquireLock(blockId) {
    debugLog('[LockManager] acquireLock called for blockId:', blockId)
    debugLog('[LockManager] subscription exists:', !!this.subscription)
    debugLog('[LockManager] pageId:', this.pageId)
    debugLog('[LockManager] currentAdminId:', this.currentAdminId)

    return new Promise((resolve) => {
      // Already hold this lock
      if (this.myLocks.has(blockId)) {
        debugLog('[LockManager] Already hold lock for:', blockId)
        resolve(true)
        return
      }

      // Check if locked by someone else
      const existingLock = this.locks.get(blockId)
      if (existingLock && !existingLock.owned) {
        debugLog('[LockManager] Block locked by other:', blockId, existingLock.admin_name)
        resolve(false)
        return
      }

      // Store pending request
      this.pendingLockRequests.set(blockId, { resolve, reject: () => resolve(false) })
      debugLog('[LockManager] Pending request stored, calling subscription.perform...')

      // Request lock from server
      if (this.subscription) {
        debugLog('[LockManager] Sending acquire_lock to server for block:', blockId)
        this.subscription.perform('acquire_lock', { block_id: blockId })
      } else {
        debugLog('[LockManager] ERROR: No subscription available!')
        this.pendingLockRequests.delete(blockId)
        resolve(false)
        return
      }

      // Timeout after 5s
      setTimeout(() => {
        if (this.pendingLockRequests.has(blockId)) {
          this.pendingLockRequests.delete(blockId)
          console.warn('[LockManager] Lock request timeout for:', blockId)
          resolve(false)
        }
      }, 5000)
    })
  }

  /**
   * Release a lock on a block
   * Only releases if no pending saves in SaveQueue for this block (unless forced)
   * @param {string} blockId - Block UUID
   * @param {boolean} force - P1-8 FIX: If true, release even with pending saves (for disconnect)
   * @returns {boolean} True if release was initiated
   */
  releaseLock(blockId, force = false) {
    // Don't hold this lock
    if (!this.myLocks.has(blockId)) {
      return false
    }

    // Check SaveQueue before releasing (skip check if forced)
    if (!force && this.saveQueue && this.hasPendingSaves(blockId)) {
      debugLog('[LockManager] Cannot release - pending saves for:', blockId)
      return false
    }

    // Clear extend interval
    if (this.extendIntervals.has(blockId)) {
      clearInterval(this.extendIntervals.get(blockId))
      this.extendIntervals.delete(blockId)
    }

    // Release on server
    this.subscription?.perform('release_lock', { block_id: blockId })

    // Update local state immediately (server will broadcast confirmation)
    this.myLocks.delete(blockId)
    this.locks.delete(blockId)
    this.notifyLockChange()

    debugLog('[LockManager] Released lock for:', blockId, force ? '(forced)' : '')
    return true
  }

  /**
   * Extend a lock on a block (heartbeat)
   * @param {string} blockId - Block UUID
   */
  extendLock(blockId) {
    if (!this.myLocks.has(blockId)) {
      return
    }

    this.subscription?.perform('extend_lock', { block_id: blockId })
  }

  /**
   * Check if block is locked by another admin
   * @param {string} blockId - Block UUID
   * @returns {boolean}
   */
  isLockedByOther(blockId) {
    const lock = this.locks.get(blockId)
    return lock && !lock.owned
  }

  /**
   * Get lock holder info for a block
   * @param {string} blockId - Block UUID
   * @returns {LockInfo|null}
   */
  getLockHolder(blockId) {
    return this.locks.get(blockId) || null
  }

  /**
   * Check if we hold a lock on a block
   * @param {string} blockId - Block UUID
   * @returns {boolean}
   */
  holdsLock(blockId) {
    return this.myLocks.has(blockId)
  }

  /**
   * Get all blocks locked by other admins
   * @returns {LockInfo[]}
   */
  getOtherLocks() {
    return Array.from(this.locks.values()).filter((lock) => !lock.owned)
  }

  /**
   * Subscribe to lock changes
   * @param {Function} callback - (locks: Map<string, LockInfo>) => void
   * @returns {Function} Unsubscribe function
   */
  onLockChange(callback) {
    this.lockChangeListeners.add(callback)
    return () => this.lockChangeListeners.delete(callback)
  }

  /**
   * Set SaveQueue reference for release checks
   * @param {Object} saveQueue - SaveQueue instance
   */
  setSaveQueue(saveQueue) {
    this.saveQueue = saveQueue
  }

  // Private methods

  /**
   * Handle incoming WebSocket message
   * @private
   */
  handleMessage(data) {
    debugLog('[LockManager] Received message:', data.event)

    switch (data.event) {
      case 'locks_state':
        this.handleLocksState(data.locks)
        break
      case 'lock_acquired':
        this.handleLockAcquired(data)
        break
      case 'lock_released':
        this.handleLockReleased(data)
        break
      case 'lock_denied':
        this.handleLockDenied(data)
        break
      case 'lock_expired':
        this.handleLockExpired(data)
        break
      case 'lock_extended':
        // P0-6 FIX: Handle lock extension broadcasts
        this.handleLockExtended(data)
        break
      default:
        debugLog('[LockManager] Unknown event:', data.event)
    }
  }

  /**
   * Handle initial locks state
   * @private
   */
  handleLocksState(locks) {
    this.locks.clear()
    this.myLocks.clear()

    // P0-4 FIX: Clear all expiration timeouts before repopulating
    for (const [, timerId] of this.expirationTimeouts) {
      clearTimeout(timerId)
    }
    this.expirationTimeouts.clear()

    for (const lock of locks) {
      this.locks.set(lock.block_id, lock)
      if (lock.owned) {
        this.myLocks.add(lock.block_id)
        this.setupExtendInterval(lock.block_id)
      } else {
        // P0-4 FIX: Setup client-side expiration for locks held by others
        this.setupExpirationTimeout(lock.block_id, lock.expires_at)
      }
    }

    debugLog('[LockManager] Locks state:', this.locks.size, 'locks,', this.myLocks.size, 'owned')
    this.notifyLockChange()
  }

  /**
   * Handle lock_acquired event
   * @private
   */
  handleLockAcquired(data) {
    debugLog('[LockManager] Lock acquired:', data.block_id, 'by:', data.admin_name)

    const lock = {
      block_id: data.block_id,
      admin_id: data.admin_id,
      admin_name: data.admin_name,
      expires_at: data.expires_at,
      owned: data.admin_id === this.currentAdminId
    }

    this.locks.set(data.block_id, lock)

    if (lock.owned) {
      this.myLocks.add(data.block_id)
      this.setupExtendInterval(data.block_id)
      // P0-4 FIX: Clear any expiration timeout for our own locks
      this.clearExpirationTimeout(data.block_id)

      // Resolve pending request
      const pending = this.pendingLockRequests.get(data.block_id)
      if (pending) {
        this.pendingLockRequests.delete(data.block_id)
        pending.resolve(true)
      }

      debugLog('[LockManager] Acquired own lock for:', data.block_id)
    } else {
      // P0-4 FIX: Setup client-side expiration for locks held by others
      this.setupExpirationTimeout(data.block_id, data.expires_at)
      debugLog('[LockManager] Block', data.block_id, 'locked by:', data.admin_name)
    }

    this.notifyLockChange()
  }

  /**
   * Handle lock_released event
   * @private
   */
  handleLockReleased(data) {
    this.locks.delete(data.block_id)
    // P0-4 FIX: Clear expiration timeout when lock is released
    this.clearExpirationTimeout(data.block_id)

    if (data.admin_id === this.currentAdminId) {
      this.myLocks.delete(data.block_id)

      // Clear extend interval
      if (this.extendIntervals.has(data.block_id)) {
        clearInterval(this.extendIntervals.get(data.block_id))
        this.extendIntervals.delete(data.block_id)
      }
    }

    debugLog('[LockManager] Lock released for:', data.block_id)
    this.notifyLockChange()
  }

  /**
   * Handle lock_denied event (our request was denied)
   * @private
   */
  handleLockDenied(data) {
    debugLog('[LockManager] Lock denied for:', data.block_id, 'held by', data.holder_name)

    // Update locks map with holder info
    this.locks.set(data.block_id, {
      block_id: data.block_id,
      admin_id: data.holder_id,
      admin_name: data.holder_name,
      expires_at: data.expires_at,
      owned: false
    })

    // Reject pending request
    const pending = this.pendingLockRequests.get(data.block_id)
    if (pending) {
      this.pendingLockRequests.delete(data.block_id)
      pending.resolve(false)
    }

    this.notifyLockChange()
  }

  /**
   * Handle lock_expired event (our lock expired)
   * @private
   */
  handleLockExpired(data) {
    console.warn('[LockManager] Lock expired for:', data.block_id)

    this.myLocks.delete(data.block_id)
    this.locks.delete(data.block_id)

    // Clear extend interval
    if (this.extendIntervals.has(data.block_id)) {
      clearInterval(this.extendIntervals.get(data.block_id))
      this.extendIntervals.delete(data.block_id)
    }

    this.notifyLockChange()
  }

  /**
   * P0-6 FIX: Handle lock_extended event (lock owner renewed their lock)
   * Updates the expires_at and reschedules client-side expiration timeout
   * P1-3 FIX: Re-creates untracked locks from the extension payload for robustness
   * @private
   */
  handleLockExtended(data) {
    let lock = this.locks.get(data.block_id)

    // P1-3 FIX: If lock not tracked, re-create it from the extension payload
    // This handles edge cases where local state was dropped due to race/delay
    if (!lock) {
      const isOwned = data.admin_id === this.currentAdminId
      lock = {
        block_id: data.block_id,
        admin_id: data.admin_id,
        admin_name: data.admin_name,
        expires_at: data.expires_at,
        owned: isOwned
      }
      this.locks.set(data.block_id, lock)

      if (isOwned) {
        this.myLocks.add(data.block_id)
        this.setupExtendInterval(data.block_id)
      } else {
        this.setupExpirationTimeout(data.block_id, data.expires_at)
      }

      debugLog('[LockManager] Re-created lock from extension event:', data.block_id, 'owned:', isOwned)
      this.notifyLockChange()
      return
    }

    // Update expires_at
    lock.expires_at = data.expires_at
    this.locks.set(data.block_id, lock)

    // If this is someone else's lock, reschedule the expiration timeout
    if (!lock.owned) {
      debugLog('[LockManager] Rescheduling expiration for extended lock:', data.block_id, 'new expires_at:', data.expires_at)
      this.setupExpirationTimeout(data.block_id, data.expires_at)
    }

    // Don't notify lock change - nothing visible changed, just the timeout
  }

  /**
   * Setup auto-extend interval for a lock
   * @private
   */
  setupExtendInterval(blockId) {
    // Clear existing interval
    if (this.extendIntervals.has(blockId)) {
      clearInterval(this.extendIntervals.get(blockId))
    }

    // Set up new interval (20s)
    const timerId = setInterval(() => {
      this.extendLock(blockId)
    }, LOCK_EXTEND_INTERVAL)

    this.extendIntervals.set(blockId, timerId)
  }

  /**
   * P0-4 FIX: Setup client-side expiration timeout for a lock
   * Auto-removes locks from UI when expires_at passes
   * @private
   */
  setupExpirationTimeout(blockId, expiresAt) {
    // Clear existing timeout
    if (this.expirationTimeouts.has(blockId)) {
      clearTimeout(this.expirationTimeouts.get(blockId))
    }

    const expireTime = new Date(expiresAt).getTime()
    const now = Date.now()
    const delay = Math.max(0, expireTime - now + 1000) // +1s buffer

    if (delay > 60000) {
      // Skip if more than 60s away - server should refresh before then
      return
    }

    const timerId = setTimeout(() => {
      debugLog('[LockManager] Client-side lock expiration for:', blockId)
      this.expirationTimeouts.delete(blockId)
      this.locks.delete(blockId)
      this.notifyLockChange()
    }, delay)

    this.expirationTimeouts.set(blockId, timerId)
  }

  /**
   * P0-4 FIX: Clear expiration timeout for a lock
   * @private
   */
  clearExpirationTimeout(blockId) {
    if (this.expirationTimeouts.has(blockId)) {
      clearTimeout(this.expirationTimeouts.get(blockId))
      this.expirationTimeouts.delete(blockId)
    }
  }

  /**
   * Check if SaveQueue has pending saves for a block
   * @private
   */
  hasPendingSaves(blockId) {
    if (!this.saveQueue) return false

    const status = this.saveQueue.getStatus()
    if (!status.hasUnsavedChanges) return false

    // Check if this specific block has pending saves
    // SaveQueue tracks by block ID in its queue Map
    // We need to check both pending queue and processing queue
    return this.saveQueue.queue?.has(blockId) ||
           this.saveQueue.processingQueue?.some((item) => item.blockId === blockId)
  }

  /**
   * Notify all lock change listeners
   * @private
   */
  notifyLockChange() {
    debugLog('[LockManager] notifyLockChange called, listeners:', this.lockChangeListeners.size)
    debugLog('[LockManager] Current locks:', this.locks.size, 'myLocks:', this.myLocks.size)
    for (const callback of this.lockChangeListeners) {
      try {
        callback(this.locks, this.myLocks)
      } catch (error) {
        console.error('[LockManager] Listener error:', error)
      }
    }
  }
}
